import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { EyeOutlined, StopOutlined } from '@ant-design/icons-vue'
import PageTitleBar from '@/components/PageTitleBar.vue'
import TableSettings from '@/types/tableSettings'
import { ls } from '@/ls'
import Column from '@/types/column'
import { constructionSiteRegistrationStore } from '@/stores/constructionSiteRegistration'
import DenyConstructionWorker from '@/components/DenyConstructionWorker.vue'
import FilterBox from '@/components/FilterBox.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ConstructionSiteRegistrations',
  setup(__props) {

const route = useRoute()
const router = useRouter()

const project = ref(null)

const columns = ref([
  {
    title: 'Medewerker',
    key: 'construction_worker.id',
    sorter: true
  },
  {
    title: 'Bedrijf',
    key: 'company.id',
    filters: [],
    sorter: true
  },
  {
    title: 'Datum',
    key: 'created_at',
    sorter: true
  },
  {
    title: 'Actie\'s',
    key: 'actions'
  }
])

onMounted(() => {
  getProject()
  constructionSiteRegistrationStore().getConstructionSiteRegistrations(tableSettings.value, route.params.projectId)
  getFilters()
  onTableSettingsChange()
})

function getProject () : void {
  axios.get(process.env.VUE_APP_API_URL + '/projects/' + route.params.projectId).then((r) => {
    project.value = r.data.data
  })
}

const tableSettings = ref<TableSettings>(ls.get('constructionSiteRegistrationsTableSettings') || {
  pagination: { current: 1, pageSize: 25 },
  showSizeChanger: true,
  pageSizeOptions: ['25', '50', '100'],
  activeFilters: null,
  sort: null,
  search: null
})

watch(() => tableSettings, () => {
  onTableSettingsChange()
}, { deep: true })

function handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
  // Remove key from filters if filter is an empty object.
  if (filters && Object.keys(filters).length) {
    Object.keys(filters).forEach((key) => {
      if (!filters[key] || !filters[key].length) {
        delete filters[key]

        columns.value.forEach((column:Column) => {
          if (column.key === key) {
            column.filteredValue = null
          }
        })
      }
    })
  }

  // Set pagination, filters and sorting on tableSettings.
  tableSettings.value.pagination = pagination
  tableSettings.value.activeFilters = filters
  tableSettings.value.sort = sort.order ? sort : null

  // Get list based on filters and search term.
  constructionSiteRegistrationStore().getConstructionSiteRegistrations(tableSettings.value, route.params.projectId)
}

function handleSearchChange (searchTerm:string) : void {
  tableSettings.value.search = searchTerm

  // Set page to 1 because of new search but retain filters and sorting.
  tableSettings.value.pagination.current = 1

  // Get list based on filters and search term.
  constructionSiteRegistrationStore().getConstructionSiteRegistrations(tableSettings.value, route.params.projectId)
}

function onTableSettingsChange () : void {
  // On tableSettings change, store them in local storage.
  ls.set('constructionSiteRegistrationsTableSettings', tableSettings.value)

  axios.post(process.env.VUE_APP_API_URL + '/users/constructionSiteApp/settings', {
    settingName: 'constructionSiteRegistrationsTableSettings',
    settingValue: tableSettings.value
  })

  // Set the current active filters on the columns.
  columns.value.forEach((column:Column) => {
    // Set active filters.
    if (tableSettings.value.activeFilters && column.key in tableSettings.value.activeFilters) {
      column.filteredValue = tableSettings.value.activeFilters[column.key]
    } else {
      column.filteredValue = null
    }

    // Set active sort order.
    if (tableSettings.value.sort && column.key === tableSettings.value.sort.columnKey) {
      column.defaultSortOrder = tableSettings.value.sort.order
      column.sortOrder = tableSettings.value.sort.order
    } else {
      column.sortOrder = false
    }
  })
}

function getFilters () : void {
  getCompanyFilter()
}

function getCompanyFilter () : void {
  axios.post(process.env.VUE_APP_API_URL + '/filters/company/construction_site_registrations', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
    columns.value[1].filters = res.data
  })
}

function updateFilters (activeFilters:{[key: string]: number[]|string[]}) : void {
  // Set updated filters.
  tableSettings.value.activeFilters = activeFilters

  // Get list based on filters and search term.
  constructionSiteRegistrationStore().getConstructionSiteRegistrations(tableSettings.value, route.params.projectId)
}

const itemList = computed(() => {
  return constructionSiteRegistrationStore().constructionSiteRegistrationsList
})

const pagination = computed(() => {
  return {
    current: tableSettings.value.pagination.current,
    defaultPageSize: tableSettings.value.pagination.pageSize,
    pageSize: parseInt(itemList.value.meta.per_page),
    total: itemList.value.meta.total,
    showTotal: (total:number, range:number[]) => `Bouwplaatsregistratie ${range[0]} t/m ${range[1]} van de ${total}`,
    showSizeChanger: tableSettings.value.showSizeChanger,
    pageSizeOptions: tableSettings.value.pageSizeOptions
  }
})

const loading = computed(() => {
  return constructionSiteRegistrationStore().loading.list
})

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_card = _resolveComponent("a-card")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageTitleBar, null, _createSlots({
      buttons: _withCtx(() => [
        _createVNode(_component_a_button, {
          type: "primary",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'ConstructionSiteRegistrationCreate', params: { projectId: _unref(route).params.projectId } })))
        }, {
          default: _withCtx(() => [
            _createTextVNode("Nieuwe registratie")
          ]),
          _: 1
        }),
        _createVNode(_component_a_button, {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push({ name: 'Dashboard' })))
        }, {
          default: _withCtx(() => [
            _createTextVNode("Terug naar overzicht")
          ]),
          _: 1
        })
      ]),
      search: _withCtx(() => [
        _createVNode(_component_a_input_search, {
          placeholder: "Zoeken..",
          style: {"width":"100%"},
          value: tableSettings.value.search,
          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((tableSettings.value.search) = $event)),
          onSearch: handleSearchChange,
          "enter-button": "",
          "allow-clear": ""
        }, null, 8, ["value"])
      ]),
      _: 2
    }, [
      (project.value)
        ? {
            name: "title",
            fn: _withCtx(() => [
              _createTextVNode(_toDisplayString(project.value.name) + " (" + _toDisplayString(project.value.reference) + ").", 1)
            ]),
            key: "0"
          }
        : {
            name: "title",
            fn: _withCtx(() => [
              _createTextVNode("Aan het laden..")
            ]),
            key: "1"
          }
    ]), 1024),
    _createVNode(FilterBox, {
      view: "registrations",
      "active-filters": tableSettings.value.activeFilters,
      columns: columns.value,
      onUpdate: updateFilters
    }, null, 8, ["active-filters", "columns"]),
    _createVNode(_component_a_row, { gutter: [16,16] }, {
      default: _withCtx(() => [
        _createVNode(_component_a_col, { span: 24 }, {
          default: _withCtx(() => [
            _createVNode(_component_a_card, { title: "Recente registraties" }, {
              default: _withCtx(() => [
                _createVNode(_component_a_table, {
                  columns: columns.value,
                  "data-source": _unref(itemList).data,
                  loading: _unref(loading),
                  pagination: _unref(pagination),
                  onChange: handleTableChange
                }, {
                  bodyCell: _withCtx(({ column, record }) => [
                    (column.key === 'construction_worker.id')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          (record.construction_worker.denied.is_denied)
                            ? (_openBlock(), _createBlock(_component_a_tooltip, {
                                key: 0,
                                placement: "right"
                              }, {
                                title: _withCtx(() => [
                                  _createTextVNode("Toegang geweigerd door " + _toDisplayString(record.construction_worker.denied.denied_by_user.full_name) + " op " + _toDisplayString(new Date(record.construction_worker.denied.denied_at).toLocaleString('nl-NL')), 1)
                                ]),
                                default: _withCtx(() => [
                                  _createVNode(_unref(StopOutlined), { style: { color: 'red', marginRight: '8px' } })
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true),
                          _createTextVNode(" " + _toDisplayString(record.construction_worker.firstname) + " " + _toDisplayString(record.construction_worker.lastname), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (column.key === 'company.id')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(_toDisplayString(record.subcontractor_company_name ? record.subcontractor_company_name + ' (ingehuurd door ' + record.company.name + ')' : record.company.name), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (column.key === 'created_at')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createTextVNode(_toDisplayString(record.created_at ? new Date(record.created_at).toLocaleString('nl-NL') : '-'), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (column.key === 'actions')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                          _createVNode(_component_a_button, {
                            style: {"margin-right":"4px"},
                            onClick: ($event: any) => (_unref(router).push({ name: 'ConstructionWorkerShow', params: { constructionWorkerId: record.construction_worker_id } }))
                          }, {
                            icon: _withCtx(() => [
                              _createVNode(_unref(EyeOutlined))
                            ]),
                            _: 2
                          }, 1032, ["onClick"]),
                          _createVNode(DenyConstructionWorker, {
                            "construction-worker": record.construction_worker,
                            "show-undo": "",
                            icon: "",
                            onUpdate: _cache[3] || (_cache[3] = ($event: any) => (_unref(constructionSiteRegistrationStore)().getConstructionSiteRegistrations(tableSettings.value, _unref(route).params.projectId)))
                          }, null, 8, ["construction-worker"])
                        ], 64))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["columns", "data-source", "loading", "pagination"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})